import React, { useState, useEffect } from 'react';
import api from '../api';

export default function Reallocation() {
  const [team, setTeam]       = useState([]);
  const [tasks, setTasks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [overrides, setOverrides] = useState({}); // taskId → assignee name
  const [confirming, setConfirming] = useState({}); // taskId → bool
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [msg, setMsg] = useState(null); // { text, type }

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [teamRes, tasksRes] = await Promise.all([
        api.get('/team'),
        api.get('/tasks'),
      ]);
      setTeam(teamRes.data.data || []);
      setTasks(tasksRes.data.data || []);
    } catch {} finally { setLoading(false); }
  };

  // Members marked absent
  const absentMembers = team.filter(m => m.availability === 'absent');

  // Available members (not absent)
  const availableMembers = team.filter(m => m.availability === 'available');

  // Open tasks for absent members
  const absentTasks = tasks.filter(t =>
    absentMembers.some(m => m.name === t.assignee) &&
    !['Done', 'Locked'].includes(t.status)
  );

  // Task load per member (open task count)
  const taskLoad = {};
  team.forEach(m => {
    taskLoad[m.name] = tasks.filter(t =>
      t.assignee === m.name && !['Done', 'Locked'].includes(t.status)
    ).length;
  });

  // Smart suggestion: available member with lowest task load
  const suggest = (forTask) => {
    const candidates = availableMembers.filter(m => m.name !== forTask.assignee);
    if (!candidates.length) return null;
    return candidates.reduce((best, m) =>
      (taskLoad[m.name] || 0) < (taskLoad[best.name] || 0) ? m : best
    );
  };

  // Build suggestions list with overrides applied
  const suggestions = absentTasks.map(task => {
    const suggested = suggest(task);
    const assignedTo = overrides[task.id] || (suggested ? suggested.name : '');
    return { task, suggested, assignedTo };
  });

  const flash = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3500);
  };

  const confirmOne = async (taskId, newAssignee) => {
    if (!newAssignee) return;
    setConfirming(c => ({ ...c, [taskId]: true }));
    try {
      await api.put(`/tasks/${taskId}`, { assignee: newAssignee });
      const task = tasks.find(t => t.id === taskId);
      const entry = {
        id: Date.now(),
        date: new Date().toLocaleDateString('en-IN'),
        absent: task?.assignee || '—',
        tasks: 1,
        to: newAssignee,
        status: 'Confirmed',
      };
      setHistory(h => [entry, ...h]);
      flash(`Task reassigned to ${newAssignee}`);
      await loadData();
    } catch {
      flash('Failed to reassign task.', 'error');
    } finally {
      setConfirming(c => ({ ...c, [taskId]: false }));
    }
  };

  const confirmAll = async () => {
    const pending = suggestions.filter(s => s.assignedTo);
    if (!pending.length) return;
    setConfirmingAll(true);
    try {
      for (const s of pending) {
        await api.put(`/tasks/${s.task.id}`, { assignee: s.assignedTo });
      }
      // Group by absent member for history
      const byAbsent = {};
      pending.forEach(s => {
        const key = `${s.task.assignee}→${s.assignedTo}`;
        if (!byAbsent[key]) byAbsent[key] = { absent: s.task.assignee, to: s.assignedTo, count: 0 };
        byAbsent[key].count++;
      });
      const newEntries = Object.values(byAbsent).map(e => ({
        id: Date.now() + Math.random(),
        date: new Date().toLocaleDateString('en-IN'),
        absent: e.absent,
        tasks: e.count,
        to: e.to,
        status: 'Confirmed',
      }));
      setHistory(h => [...newEntries, ...h]);
      setOverrides({});
      flash(`${pending.length} task${pending.length !== 1 ? 's' : ''} reallocated successfully`);
      await loadData();
    } catch {
      flash('Some reallocations failed.', 'error');
    } finally {
      setConfirmingAll(false); }
  };

  const PRIORITY_COLOR = { urgent: '#ef4444', high: '#f59e0b', normal: '#3b7ef0', low: '#22c55e' };
  const STATUS_COLOR   = { 'In Progress': '#3b7ef0', Todo: '#6b7280', Review: '#f59e0b', Blocked: '#ef4444' };

  if (loading) return (
    <div className="page-wrap">
      <div className="empty-state"><div className="empty-state-title">Loading...</div></div>
    </div>
  );

  return (
    <div className="page-wrap">

      {/* Flash message */}
      {msg && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: '0.85rem', fontWeight: 600,
          background: msg.type === 'error' ? '#fef2f2' : '#f0fdf4',
          color:      msg.type === 'error' ? '#b91c1c'  : '#15803d',
          border:     `1px solid ${msg.type === 'error' ? '#fca5a5' : '#86efac'}`,
        }}>
          {msg.type === 'error' ? '⚠️' : '✅'} {msg.text}
        </div>
      )}

      {/* Engine alert — Refresh Engine button lives here */}
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)',
        borderLeft: '4px solid var(--red, #ef4444)',
        borderRadius: 10, padding: '14px 18px', marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', maxWidth: 500 }}>
          ⚠️ When a team member is marked absent, the system automatically suggests work redistribution
          based on availability and current workload. Review and confirm the suggested changes.
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          {suggestions.filter(s => s.assignedTo).length > 0 && (
            <button
              className="btn btn-primary"
              onClick={confirmAll}
              disabled={confirmingAll}
            >
              {confirmingAll ? 'Confirming...' : `✓ Confirm All (${suggestions.filter(s => s.assignedTo).length})`}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={loadData}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            🔀 Refresh
          </button>
        </div>
      </div>

      {/* Two column: Absent Today + Suggestions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, marginBottom: 16 }}>

        {/* Absent Today */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">🔴 Absent Today</div>
          </div>
          {absentMembers.length === 0 ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
              No absences today 🎉
            </div>
          ) : absentMembers.map(m => {
            const openCount = tasks.filter(t =>
              t.assignee === m.name && !['Done', 'Locked'].includes(t.status)
            ).length;
            return (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 0', borderBottom: '1px solid var(--border)',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: m.color || '#6c58f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
                }}>
                  {m.initials || (m.name||'').slice(0,2).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text)' }}>{m.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{m.designation || m.role}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px',
                    borderRadius: 20, background: '#fee2e2', color: '#b91c1c',
                  }}>Absent</span>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 3 }}>
                    {openCount} open task{openCount !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Available members workload */}
          {availableMembers.length > 0 && (
            <>
              <div style={{ margin: '14px 0 8px', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Available Team
              </div>
              {availableMembers.map(m => {
                const load = taskLoad[m.name] || 0;
                const maxLoad = Math.max(...availableMembers.map(x => taskLoad[x.name] || 0), 1);
                return (
                  <div key={m.id} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text)' }}>{m.name}</span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{load} tasks</span>
                    </div>
                    <div style={{ height: 5, borderRadius: 3, background: 'var(--border)' }}>
                      <div style={{
                        height: '100%', borderRadius: 3,
                        width: `${Math.round((load / maxLoad) * 100)}%`,
                        background: load > 5 ? '#ef4444' : load > 3 ? '#f59e0b' : '#22c55e',
                        transition: 'width 0.3s',
                      }} />
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Suggested Reallocations */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">📋 Suggested Reallocations</div>
            {suggestions.length > 0 && (
              <span style={{
                fontSize: '0.72rem', padding: '2px 10px', borderRadius: 20,
                background: '#eff6ff', color: '#1d4ed8', fontWeight: 700,
              }}>
                {suggestions.length} task{suggestions.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {suggestions.length === 0 ? (
            <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              {absentMembers.length === 0
                ? 'No absent members — nothing to reallocate.'
                : 'No open tasks found for absent members.'}
            </div>
          ) : suggestions.map(({ task, suggested, assignedTo }) => (
            <div key={task.id} style={{
              padding: '12px 0',
              borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              {/* Task info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text)', marginBottom: 3 }}>
                  {task.title}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                  {task.client && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>🏢 {task.client}</span>
                  )}
                  {task.priority && (
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 700, padding: '1px 7px',
                      borderRadius: 20, color: '#fff',
                      background: PRIORITY_COLOR[task.priority] || '#6b7280',
                    }}>
                      {task.priority}
                    </span>
                  )}
                  {task.status && (
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 600, padding: '1px 7px',
                      borderRadius: 20, border: `1px solid ${STATUS_COLOR[task.status] || '#6b7280'}44`,
                      color: STATUS_COLOR[task.status] || '#6b7280',
                    }}>
                      {task.status}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  <span style={{ color: '#ef4444', fontWeight: 600 }}>From: {task.assignee}</span>
                  {suggested && (
                    <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>
                      → Suggested: <span style={{ color: '#22c55e', fontWeight: 600 }}>{suggested.name}</span>
                      {' '}({taskLoad[suggested.name] || 0} tasks)
                    </span>
                  )}
                </div>
              </div>

              {/* Override dropdown + confirm */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <select
                  className="form-select"
                  style={{ fontSize: '0.78rem', padding: '5px 8px', minWidth: 140 }}
                  value={assignedTo}
                  onChange={e => setOverrides(o => ({ ...o, [task.id]: e.target.value }))}
                >
                  <option value="">— Select —</option>
                  {team
                    .filter(m => m.name !== task.assignee)
                    .map(m => (
                      <option key={m.id} value={m.name}>
                        {m.name} ({taskLoad[m.name] || 0} tasks){m.availability !== 'available' ? ' ⚠️' : ''}
                      </option>
                    ))
                  }
                </select>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: '0.75rem', padding: '5px 12px', flexShrink: 0, opacity: (!assignedTo || confirming[task.id]) ? 0.5 : 1 }}
                  disabled={!assignedTo || confirming[task.id]}
                  onClick={() => confirmOne(task.id, assignedTo)}
                >
                  {confirming[task.id] ? '...' : 'Confirm'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* History Table */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">📜 Reallocation History</div>
        </div>
        {history.length === 0 ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
            No reallocations recorded this session.
          </div>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Absent Member</th>
                <th>Tasks Moved</th>
                <th>Reallocated To</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id}>
                  <td style={{ fontSize: '0.8rem' }}>{h.date}</td>
                  <td>
                    <span style={{ fontWeight: 600, color: '#ef4444', fontSize: '0.82rem' }}>{h.absent}</span>
                  </td>
                  <td style={{ fontSize: '0.82rem', textAlign: 'center' }}>{h.tasks}</td>
                  <td>
                    <span style={{ fontWeight: 600, color: '#22c55e', fontSize: '0.82rem' }}>{h.to}</span>
                  </td>
                  <td>
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 700, padding: '2px 10px',
                      borderRadius: 20, background: '#dcfce7', color: '#15803d',
                    }}>
                      {h.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
